import { useMemo } from 'react';
import { normalizeLocale, useSiteConfig, useTranslation } from '@bms/common';
import type { Room } from '@bms/common';
// The pure formatters come from the utils entry, which component tests leave unmocked.
import { createSiteDates, formatSiteDateTime, readSiteDateFormat } from '@bms/common/utils';
import type { SiteDateOptions } from '@bms/common/utils';

/**
 * The house checkout time when `site_config.settings.grms.checkout_time` is absent
 * (plan-frontDeskUx A6; BMS-spec GRMS §14.2). The one place the default lives:
 * the check-in dialog, the Today strip and the stays board all read it through
 * `houseCheckoutTime()`.
 */
export const DEFAULT_CHECKOUT_TIME = '11:00';

/** `HH:MM`, 24-hour — the same pattern `PATCH /api/site/checkout-time` enforces. */
export const CHECKOUT_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/** The site's house checkout time, `HH:MM` in the site's own zone. */
export function houseCheckoutTime(settings: Record<string, unknown> | undefined | null): string {
  const grms = settings?.grms;
  const value = grms && typeof grms === 'object' && !Array.isArray(grms)
    ? (grms as Record<string, unknown>).checkout_time
    : undefined;
  return typeof value === 'string' && CHECKOUT_TIME_PATTERN.test(value) ? value : DEFAULT_CHECKOUT_TIME;
}

/**
 * Whether a room can be sold right now (A1): active, vacant, housekeeping `ready`,
 * in service, and no open alarm. The walk-in room picker offers only these.
 */
export function isSellable(room: Room, openAlarmRoomCodes: ReadonlySet<string>): boolean {
  return room.status === 'active'
    && (room.occupancyStatus ?? 'vacant') === 'vacant'
    && room.housekeepingStatus === 'ready'
    && (room.serviceState ?? 'in_service') === 'in_service'
    && !openAlarmRoomCodes.has(room.roomCode);
}

export interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

/** A zone `Intl` accepts, or undefined (the browser's own) when it does not. */
function usableZone(timeZone: string | undefined): string | undefined {
  if (!timeZone) return undefined;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return timeZone;
  } catch {
    return undefined;
  }
}

/** The site-local calendar date and time an instant falls on in `timeZone`. */
export function wallClockAt(instant: Date, timeZone: string | undefined): WallClock {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value);
  return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour'), minute: get('minute') };
}

/** Milliseconds the zone is ahead of UTC at `instant`. */
function zoneOffsetMs(instant: Date, timeZone: string | undefined): number {
  const wall = wallClockAt(instant, timeZone);
  const asUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute);
  return asUtc - Math.floor(instant.getTime() / 60_000) * 60_000;
}

/** The instant a wall-clock time in `timeZone` names (DST-safe to the minute). */
function wallClockToInstant(wall: WallClock, timeZone: string | undefined): Date {
  const guess = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute);
  const first = guess - zoneOffsetMs(new Date(guess), timeZone);
  const second = guess - zoneOffsetMs(new Date(first), timeZone);
  return new Date(second);
}

/**
 * The expected checkout for a stay of `nights` starting today: the site-local date
 * `nights` days from today at the house checkout time, as an ISO instant.
 */
export function checkoutAfterNights(
  now: Date,
  nights: number,
  checkoutTime: string,
  timeZone: string | undefined,
): string {
  const zone = usableZone(timeZone);
  const today = wallClockAt(now, zone);
  const [hour, minute] = (CHECKOUT_TIME_PATTERN.test(checkoutTime) ? checkoutTime : DEFAULT_CHECKOUT_TIME)
    .split(':').map(Number);
  // Day arithmetic on a UTC date, so a DST change between today and checkout
  // cannot move the calendar day.
  const date = new Date(Date.UTC(today.year, today.month - 1, today.day + Math.max(0, nights)));
  return wallClockToInstant({
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
    hour,
    minute,
  }, zone).toISOString();
}

const MS_PER_DAY = 86_400_000;

/**
 * The calendar day an instant falls on in the site's zone, as whole days since
 * 1970-01-01: comparable and addable, so a week of cells is `today - 3 … today + 3`
 * with no DST arithmetic.
 */
export function siteDayNumber(instant: Date | string, timeZone: string | undefined): number {
  const wall = wallClockAt(typeof instant === 'string' ? new Date(instant) : instant, usableZone(timeZone));
  return Math.round(Date.UTC(wall.year, wall.month - 1, wall.day) / MS_PER_DAY);
}

/** The instant a site day begins, as ISO: the way back from `siteDayNumber`. */
export function siteDayStart(day: number, timeZone: string | undefined): string {
  const date = new Date(day * MS_PER_DAY);
  return wallClockToInstant({
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
    hour: 0,
    minute: 0,
  }, usableZone(timeZone)).toISOString();
}

/** A day number's label, e.g. `{ weekday: 'short' }` → "Sat", `{ day: 'numeric', month: 'short' }` → "24 Oct". */
export function formatDayNumber(day: number, locale: string, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(new Date(day * MS_PER_DAY));
}

/** The short weekday of a day number: "Sat" / "sub". */
export function formatWeekday(day: number, locale: string): string {
  return formatDayNumber(day, locale, { weekday: 'short' });
}

/** The day of the month of a day number: "24". */
export function formatDayOfMonth(day: number, locale: string): string {
  return formatDayNumber(day, locale, { day: 'numeric' });
}

/** `HH:MM`, 24-hour, in the site's zone. */
export function formatClock(iso: string, timeZone: string | undefined, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(locale, {
    timeZone: usableZone(timeZone), hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).format(date);
}

/**
 * A timestamp in the site's date format (`settings.ui.date_format`, decision D7
 * in docs/decisions/room-dialog.md): "today 08:12", "yesterday 18:40", else the
 * full date and time ("2026-10-22 14:30" by default). A later day prints in full.
 */
export function formatStamp(
  iso: string,
  now: Date,
  options: SiteDateOptions,
  words: { today: string; yesterday: string },
): string {
  const time = formatClock(iso, options.timeZone, options.locale);
  if (!time) return '';
  const ago = siteDayNumber(now, options.timeZone) - siteDayNumber(iso, options.timeZone);
  if (ago === 0) return `${words.today} ${time}`;
  if (ago === 1) return `${words.yesterday} ${time}`;
  return formatSiteDateTime(iso, options);
}

/** English prints day-month ("Mon 26 Oct"), as the site's local date format does. */
function deskLocale(locale: string): string {
  return locale === 'en' || locale.startsWith('en-') ? 'en-GB' : locale;
}

/** The weekday with the date, in the site's zone: "Mon 26 Oct" / "pon 26. lis". */
export function formatWeekdayDate(iso: string, timeZone: string | undefined, locale: string): string {
  if (Number.isNaN(new Date(iso).getTime())) return '';
  return formatWeekdayOfDay(siteDayNumber(iso, timeZone), locale);
}

/** `formatWeekdayDate` for a day number: "Mon 26 Oct" / "pon 26. lis". */
export function formatWeekdayOfDay(day: number, locale: string): string {
  return formatDayNumber(day, deskLocale(locale), { weekday: 'short', day: 'numeric', month: 'short' }).replace(',', '');
}

/** The date of a day number with no weekday: "26 Oct" / "26. lis". */
export function formatDateOfDay(day: number, locale: string): string {
  return formatDayNumber(day, deskLocale(locale), { day: 'numeric', month: 'short' });
}

/**
 * The desk's day words (F7, F13): "today", "yesterday", else the weekday with
 * the date, "Mon 26 Oct" — the strip's words, never an ISO date.
 */
export function formatDeskDay(
  iso: string,
  now: Date,
  timeZone: string | undefined,
  locale: string,
  words: { today: string; yesterday: string },
): string {
  const ago = siteDayNumber(now, timeZone) - siteDayNumber(iso, timeZone);
  if (ago === 0) return words.today;
  if (ago === 1) return words.yesterday;
  return formatWeekdayDate(iso, timeZone, locale);
}

/** The day words with the time: "today 10:12", "Mon 26 Oct 15:10". */
export function formatDeskDateTime(
  iso: string,
  now: Date,
  timeZone: string | undefined,
  locale: string,
  words: { today: string; yesterday: string },
): string {
  const time = formatClock(iso, timeZone, locale);
  if (!time) return '';
  return `${formatDeskDay(iso, now, timeZone, locale, words)} ${time}`;
}

/**
 * The site's date format, zone and the UI language, with the printers the
 * dialog uses: the site-format `date`, `dateTime` and `stamp`, and the desk's
 * day words `day` and `dayTime` ("Mon 26 Oct 15:10").
 */
export function useSiteDates() {
  const { t, i18n } = useTranslation();
  const config = useSiteConfig().data;
  const options: SiteDateOptions = {
    format: readSiteDateFormat(config?.settings),
    timeZone: config?.timezone,
    locale: normalizeLocale(i18n?.language),
  };
  const today = t('grms.stay.dayToday');
  const yesterday = t('grms.stay.dayYesterday');
  return useMemo(() => {
    const words = { today, yesterday };
    const printed = (print: typeof formatDeskDay) => (value: string | null | undefined, now: Date = new Date()) => (
      value ? print(value, now, options.timeZone, options.locale, words) || '—' : '—'
    );
    return {
      ...createSiteDates(options, words),
      day: printed(formatDeskDay),
      dayTime: printed(formatDeskDateTime),
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options.format, options.timeZone, options.locale, today, yesterday]);
}

/**
 * How long ago an instant was, in the words a desk uses: today, yesterday, a
 * weekday within the last week, else a short date. `label` is the weekday or
 * date and is empty for today and yesterday, which the caller words itself.
 */
export function relativeDay(
  instant: string,
  now: Date,
  timeZone: string | undefined,
  locale: string,
): { kind: 'today' | 'yesterday' | 'weekday' | 'date'; label: string } {
  const day = siteDayNumber(instant, timeZone);
  const ago = siteDayNumber(now, timeZone) - day;
  if (ago <= 0) return { kind: 'today', label: '' };
  if (ago === 1) return { kind: 'yesterday', label: '' };
  if (ago < 7) return { kind: 'weekday', label: formatDayNumber(day, locale, { weekday: 'short' }) };
  return { kind: 'date', label: formatDayNumber(day, locale, { day: 'numeric', month: 'short' }) };
}

/** Whole nights from today (site-local) to the checkout date, never below 0. */
export function nightsUntil(now: Date, checkoutIso: string, timeZone: string | undefined): number {
  const zone = usableZone(timeZone);
  const today = wallClockAt(now, zone);
  const out = wallClockAt(new Date(checkoutIso), zone);
  const days = (Date.UTC(out.year, out.month - 1, out.day) - Date.UTC(today.year, today.month - 1, today.day)) / 86_400_000;
  return Math.max(0, Math.round(days));
}
